// @ts-nocheck
import _ from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { useIfMounted } from '../hooks';

export const DialogContext = createContext();

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '500px',
    marginBottom: '12px',
  },
  title: {
    '& .MuiTypography-root': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  link: {
    '& a': {
      marginLeft: theme.spacing(1),
      fontSize: '0.8rem',
      color: theme.palette.text.primary,
      textDecoration: 'underline',
      '&:hover, &:focus': {
        color: theme.palette.text.hint,
      },
    },
  },
  progress: {
    marginLeft: theme.spacing(2),
  },
}));

function DialogComponent(props) {
  const [value, setValue] = useState(undefined);
  const [disabled, setDisabled] = useState(false);
  const [t] = useTranslation();
  const classes = useStyles();

  const {
    open,
    title,
    content,
    defaultValue,
    loading,
    className,
    cancelButtonText,
    cancelButtonIcon,
    confirmButtonText,
    confirmButtonIcon,
    hideFooter,
    onClose,
    onCancel,
    onConfirm,
    link,
  } = props;

  useEffect(() => {
    setValue(defaultValue);
    setDisabled(false);
  }, [open, defaultValue]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      onClose={onClose}
      disableBackdropClick
      disableEscapeKeyDown
    >
      {title ? (
        <DialogTitle id="Dialog-title" className={classes.title}>
          <div>
            {title}
            {link && (<span className={classes.link}>{link}</span>)}
            {loading && (
              <CircularProgress id="Dialog-progress" className={classes.progress} size={24} />
            )}
          </div>
          <IconButton size="small" onClick={onCancel}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
      ) : null}
      <DialogContent id="Dialog-content" className={clsx(classes.content, className)}>
        {typeof content === 'function'
          ? content({
              value,
              setValue,
              setDisabled,
              onConfirm,
              onCancel,
            })
          : content}
      </DialogContent>
      {!hideFooter && (
        <DialogActions id="Dialog-actions">
          <Button
            id="Dialog-cancel"
            startIcon={<Icon>{_.defaultTo(cancelButtonIcon, 'cancel')}</Icon>}
            onClick={onCancel}
            disabled={loading}
          >
            {_.defaultTo(cancelButtonText, t('common.cancel'))}
          </Button>
          <Button
            id="Dialog-confirm"
            color="primary"
            startIcon={<Icon>{_.defaultTo(confirmButtonIcon, 'task_alt')}</Icon>}
            onClick={() => onConfirm(value)}
            disabled={disabled || loading}
            autoFocus
          >
            {_.defaultTo(confirmButtonText, t('common.confirm'))}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

function DialogProvider(props) {
  const [dialogStack, setDialogStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const ifMounted = useIfMounted();

  const showDialog = useCallback(
    ({
      title,
      content,
      defaultValue,
      className,
      cancelButtonText,
      cancelButtonIcon,
      confirmButtonText,
      confirmButtonIcon,
      throwOnError,
      hideOnError,
      hideFooter,
      onConfirm,
      onCancel,
      link,
      keepPreviousOpen = false,
    }) => {
      setDialogStack(prevStack => {
        const newDialog = {
          open: true,
          title,
          content,
          defaultValue,
          className,
          cancelButtonText,
          cancelButtonIcon,
          confirmButtonText,
          confirmButtonIcon,
          throwOnError,
          hideOnError,
          hideFooter,
          onConfirm,
          onCancel,
          link,
        };

        if (keepPreviousOpen) {
          return [...prevStack, newDialog];
        }

        return [newDialog];
      });
      },
    []
  );

  const hideDialog = useCallback((clearAll = false) => {
    setDialogStack((prevStack) => (clearAll ? [] : prevStack.slice(0, -1)));
  }, []);

  const handleConfirm = async value => {
    const topDialog = dialogStack[dialogStack.length - 1];

    if (!topDialog) return;
  
    setLoading(true);
    try {
      const callback = await topDialog.onConfirm?.(value);
      ifMounted(() => {
        setLoading(false);
        hideDialog(true);
      });

      await callback?.();
    } catch (e) {
      ifMounted(() => {
        setLoading(false);
        if (topDialog.hideOnError) {
          hideDialog(true);
        }
      });
      if (topDialog.throwOnError) {
        throw e;
      }
    }
  };

  const handleCancel = async () => {
    const topDialog = dialogStack[dialogStack.length - 1];

    if (!topDialog) return;

    const callback = topDialog.onCancel?.();
    hideDialog();

    await callback?.();
  };

  const { children } = props;

  return (
    <DialogContext.Provider value={{ showDialog, hideDialog }}>
      {children}
      {dialogStack.map((dialog, index) => (
        <DialogComponent 
          key={index}
          open={dialog.open}
          title={dialog.title}
          content={dialog.content}
          defaultValue={dialog.defaultValue}
          loading={loading}
          className={dialog.className}
          cancelButtonText={dialog.cancelButtonText}
          cancelButtonIcon={dialog.cancelButtonIcon}
          confirmButtonText={dialog.confirmButtonText}
          confirmButtonIcon={dialog.confirmButtonIcon}
          hideFooter={dialog.hideFooter}
          link={dialog.link}
          onClose={hideDialog}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
      />))}
    </DialogContext.Provider>
  );
}

DialogComponent.defaultProps = {
  content: undefined,
  defaultValue: undefined,
  className: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  confirmButtonText: undefined,
  confirmButtonIcon: undefined,
  loading: false,
  hideFooter: false,
  link: undefined,
};

DialogComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  className: PropTypes.string,
  loading: PropTypes.bool,
  hideFooter: PropTypes.bool,
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  confirmButtonText: PropTypes.string,
  confirmButtonIcon: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  link: PropTypes.object,
};

DialogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DialogProvider;
